import { Box, Badge, Heading, HStack, SimpleGrid, Table, Text, VStack } from "@chakra-ui/react";
import { Helmet } from "react-helmet";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import DownloadReport from "../../../components/DownloadReport";
import FilterForm from "../../../components/FilterForm";
import SelectBranchInfo from "../../../components/misc/SelectBranchInfo";
import { REPORT_RANGES, REPORT_TYPE_IDS } from "../../../store/constants";
import {
  formatAnnualDate,
  formatAnnualDateRange,
  formatAnnualValue,
  normalizeAnnualDataQualityEnvelope,
  toTitle,
} from "./annualDataQualityEnvelope";

const SOURCE_RECORD_LABELS = {
  branches: "Branches",
  reports: "Monthly Reports",
  services: "Services",
  slaRecords: "SLA Records",
  tickets: "Tickets",
  users: "Users",
};

const CONFIDENCE_ORDER = Object.freeze({
  high: 4,
  medium: 3,
  low: 2,
  unknown: 1,
});

const getPointLabel = (point, index) =>
  point?.month ||
  point?.label ||
  point?.name ||
  point?.scope ||
  point?.date ||
  `Point ${index + 1}`;

const getNumericKeys = (data = []) => {
  const keys = new Set();
  data.forEach((point) => {
    Object.entries(point || {}).forEach(([key, value]) => {
      if (typeof value === "number" && Number.isFinite(value)) {
        keys.add(key);
      }
    });
  });
  return [...keys].slice(0, 3);
};

const formatReportName = (value) => (value ? toTitle(value) : "N/A");

const getConfidenceTone = (label = "unknown") => {
  const normalized = String(label).toLowerCase();
  if (CONFIDENCE_ORDER[normalized] >= CONFIDENCE_ORDER.high) return "green";
  if (CONFIDENCE_ORDER[normalized] >= CONFIDENCE_ORDER.medium) return "yellow";
  if (CONFIDENCE_ORDER[normalized] >= CONFIDENCE_ORDER.low) return "orange";
  return "gray";
};

const MetadataSummary = ({ envelope }) => {
  const metadata = envelope.metadata || {};
  const dateRange = metadata.dateRange || {
    createdFrom: metadata.createdFrom,
    createdTo: metadata.createdTo,
  };
  const sourceRecordCounts = metadata.sourceRecordCounts || {};
  const confidence = envelope.confidence;
  const confidenceMetrics = confidence?.metrics || {};
  const confidenceReasons = Array.isArray(confidence?.reasons) ? confidence.reasons : [];
  const confidenceLists = [
    ["Missing Fields", confidence?.missingFields],
    ["Incomplete Months", confidence?.incompleteMonths],
    ["Blockers", confidence?.blockers],
  ].filter(([, values]) => Array.isArray(values) && values.length);
  const metadataItems = [
    ["Year", metadata.year],
    ["Period", formatAnnualDateRange(dateRange)],
    [
      "Generated At",
      metadata.generatedAt ? formatAnnualDate(metadata.generatedAt, { includeTime: true }) : "",
    ],
    ["Data Version", metadata.dataVersion],
    ["Report", formatReportName(metadata.reportName)],
    ["Range", metadata.reportRange ? toTitle(metadata.reportRange) : ""],
  ].filter(([, value]) => value !== undefined && value !== null && value !== "" && value !== "N/A");
  const sourceRows = Object.entries(sourceRecordCounts)
    .filter(([, value]) => value !== undefined)
    .sort(([left], [right]) => {
      const orderedKeys = Object.keys(SOURCE_RECORD_LABELS);
      const leftIndex = orderedKeys.indexOf(left);
      const rightIndex = orderedKeys.indexOf(right);
      return (leftIndex === -1 ? 100 : leftIndex) - (rightIndex === -1 ? 100 : rightIndex);
    });

  if (!metadataItems.length && !sourceRows.length && confidence === null) {
    return null;
  }

  return (
    <Box border="1px solid var(--borderGrey)" borderRadius="6px" p="12px" bg="#fff">
      <Text fontSize="13px" fontWeight="700" color="var(--darkGrey)" mb="8px">
        Report Details
      </Text>
      <SimpleGrid columns={{ base: 1, md: 3 }} gap="8px">
        {metadataItems.map(([label, value]) => (
          <Box key={label}>
            <Text fontSize="11px" color="var(--gray-8)" textTransform="uppercase">
              {label}
            </Text>
            <Text fontSize="13px" fontWeight="600">
              {value}
            </Text>
          </Box>
        ))}
      </SimpleGrid>

      {sourceRows.length ? (
        <Box mt="12px" pt="10px" borderTop="1px solid #eef0f3">
          <Text fontSize="12px" color="var(--gray-8)" fontWeight="700" mb="8px">
            Source Records
          </Text>
          <SimpleGrid columns={{ base: 2, md: 3, xl: 6 }} gap="8px">
            {sourceRows.map(([key, value]) => (
              <Box key={key} border="1px solid #eef0f3" borderRadius="5px" p="8px">
                <Text fontSize="11px" color="var(--gray-8)">
                  {SOURCE_RECORD_LABELS[key] || toTitle(key)}
                </Text>
                <Text fontSize="15px" fontWeight="700">
                  {formatAnnualValue(value)}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        </Box>
      ) : null}

      {confidence !== null ? (
        <Box mt="12px" pt="10px" borderTop="1px solid #eef0f3">
          <HStack justifyContent="space-between" alignItems="center" mb="8px" gap="8px">
            <Box>
              <Text fontSize="12px" color="var(--gray-8)" fontWeight="700">
                Confidence
              </Text>
              {confidence?.score !== undefined ? (
                <Text fontSize="12px" color="var(--gray)">
                  Score: {formatAnnualValue(confidence.score)}
                </Text>
              ) : null}
            </Box>
            <Badge colorPalette={getConfidenceTone(confidence?.label)} variant="subtle">
              {toTitle(confidence?.label || "unknown")}
            </Badge>
          </HStack>

          {Object.keys(confidenceMetrics).length ? (
            <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} gap="8px">
              {Object.entries(confidenceMetrics).map(([key, value]) => (
                <Box key={key} border="1px solid #eef0f3" borderRadius="5px" p="8px">
                  <Text fontSize="11px" color="var(--gray-8)">
                    {toTitle(key)}
                  </Text>
                  <Text fontSize="13px" fontWeight="700">
                    {toTitle(value)}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          ) : null}

          {confidenceReasons.length ? (
            <VStack alignItems="stretch" gap="4px" mt="8px">
              {confidenceReasons.slice(0, 4).map((reason, index) => (
                <Text key={`${reason}-${index}`} fontSize="12px" color="var(--gray)">
                  {reason}
                </Text>
              ))}
            </VStack>
          ) : null}

          {confidenceLists.length ? (
            <SimpleGrid columns={{ base: 1, md: 3 }} gap="8px" mt="8px">
              {confidenceLists.map(([label, values]) => (
                <Box key={label} border="1px solid #eef0f3" borderRadius="5px" p="8px">
                  <Text fontSize="11px" color="var(--gray-8)" mb="4px">
                    {label}
                  </Text>
                  <Text fontSize="12px" color="var(--black-dry)">
                    {values.slice(0, 6).map(toTitle).join(", ")}
                    {values.length > 6 ? ` +${values.length - 6} more` : ""}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};

const SummaryCards = ({ cards }) => {
  if (!cards.length) return null;

  return (
    <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} gap="10px">
      {cards.map((card) => (
        <Box key={card.id} border="1px solid var(--borderGrey)" borderRadius="6px" p="12px" bg="#fff">
          <HStack justifyContent="space-between" alignItems="flex-start" gap="8px">
            <Text fontSize="12px" color="var(--gray-8)" fontWeight="600">
              {card.label}
            </Text>
            {card.status ? (
              <Badge colorPalette="blue" variant="subtle" fontSize="10px">
                {card.status}
              </Badge>
            ) : null}
          </HStack>
          <Text fontSize="24px" lineHeight="1.15" fontWeight="700" color="var(--black)" mt="8px">
            {formatAnnualValue(card.value)}
          </Text>
          {card.description ? (
            <Text fontSize="12px" color="var(--gray)" mt="6px">
              {card.description}
            </Text>
          ) : null}
        </Box>
      ))}
    </SimpleGrid>
  );
};

const EnvelopeTable = ({ table }) => {
  const visibleRows = table.rows.slice(0, table.maxRows);

  if (!table.columns.length || !table.rows.length) {
    return (
      <Box border="1px solid var(--borderGrey)" borderRadius="6px" p="12px" bg="#fff">
        <Text fontSize="13px" fontWeight="700" color="var(--darkGrey)">
          {table.title}
        </Text>
        <Text fontSize="13px" color="var(--gray)" mt="4px">
          No table rows available.
        </Text>
      </Box>
    );
  }

  return (
    <Box border="1px solid var(--borderGrey)" borderRadius="6px" p="12px" bg="#fff">
      <HStack justifyContent="space-between" alignItems="flex-start" gap="12px" mb="8px">
        <Box>
          <Text fontSize="13px" fontWeight="700" color="var(--darkGrey)">
            {table.title}
          </Text>
          {table.description ? (
            <Text fontSize="12px" color="var(--gray)" mt="2px">
              {table.description}
            </Text>
          ) : null}
        </Box>
        <Text fontSize="11px" color="var(--gray-8)" whiteSpace="nowrap">
          {table.rows.length} rows
        </Text>
      </HStack>
      <Box className="reportDataTableScroll">
        <Table.Root className="stripedTable reportDataTable reportDataTable--compact" size="sm">
          <Table.Header>
            <Table.Row>
              {table.columns.map((column) => (
                <Table.ColumnHeader key={column.id}>{column.label}</Table.ColumnHeader>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {visibleRows.map((row, rowIndex) => (
              <Table.Row key={row.id || row.key || rowIndex}>
                {table.columns.map((column, columnIndex) => (
                  <Table.Cell key={column.id}>
                    {Array.isArray(row)
                      ? formatAnnualValue(row[columnIndex])
                      : formatAnnualValue(row?.[column.id])}
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Box>
      {table.rows.length > visibleRows.length ? (
        <Text fontSize="11px" color="var(--gray-8)" mt="6px">
          Showing first {visibleRows.length} rows.
        </Text>
      ) : null}
    </Box>
  );
};

const Warnings = ({ warnings }) => {
  if (!warnings.length) return null;

  return (
    <Box border="1px solid #f4d49a" borderRadius="6px" p="12px" bg="#fff8eb">
      <Text fontSize="13px" fontWeight="700" color="var(--darkGrey)" mb="6px">
        Warnings
      </Text>
      <VStack alignItems="stretch" gap="4px">
        {warnings.map((warning, index) => (
          <Text key={warning.id || index} fontSize="13px" color="var(--black-dry)">
            {formatAnnualValue(warning.message || warning)}
          </Text>
        ))}
      </VStack>
    </Box>
  );
};

const AnnualChart = ({ chart }) => {
  const numericKeys = getNumericKeys(chart.data);
  const primaryMetric = numericKeys[0];

  if (!chart.data.length || !primaryMetric) {
    return (
      <Box border="1px solid #eef0f3" borderRadius="5px" p="10px">
        <Text fontSize="13px" fontWeight="600">
          {chart.title}
        </Text>
        <Text fontSize="12px" color="var(--gray)" mt="2px">
          No numeric chart data was supplied.
        </Text>
      </Box>
    );
  }

  const maxValue = Math.max(...chart.data.map((point) => point[primaryMetric] || 0), 1);

  return (
    <Box border="1px solid #eef0f3" borderRadius="5px" p="10px">
      <Text fontSize="13px" fontWeight="600">
        {chart.title}
      </Text>
      {chart.description ? (
        <Text fontSize="12px" color="var(--gray)" mt="2px">
          {chart.description}
        </Text>
      ) : null}
      <VStack alignItems="stretch" gap="7px" mt="10px">
        {chart.data.slice(0, 12).map((point, index) => {
          const value = point[primaryMetric] || 0;
          const width = `${Math.max((value / maxValue) * 100, value > 0 ? 3 : 0)}%`;

          return (
            <HStack key={`${chart.id}-${index}`} gap="8px" alignItems="center">
              <Text fontSize="11px" color="var(--gray-8)" minW="72px">
                {getPointLabel(point, index)}
              </Text>
              <Box flex="1" h="8px" bg="#edf2f7" borderRadius="999px" overflow="hidden">
                <Box h="100%" width={width} bg="#2f80ed" borderRadius="999px" />
              </Box>
              <Text fontSize="11px" fontWeight="700" minW="48px" textAlign="right">
                {formatAnnualValue(value)}
              </Text>
            </HStack>
          );
        })}
      </VStack>
      <Text fontSize="11px" color="var(--gray-8)" mt="8px">
        Primary metric: {toTitle(primaryMetric)}
        {numericKeys.length > 1
          ? `; also supplied: ${numericKeys.slice(1).map(toTitle).join(", ")}`
          : ""}
      </Text>
    </Box>
  );
};

const AnnualCharts = ({ charts }) => {
  if (!charts.length) return null;

  return (
    <Box border="1px solid var(--borderGrey)" borderRadius="6px" p="12px" bg="#fff">
      <Text fontSize="13px" fontWeight="700" color="var(--darkGrey)" mb="8px">
        Charts
      </Text>
      <SimpleGrid columns={{ base: 1, lg: 2 }} gap="8px">
        {charts.map((chart) => (
          <AnnualChart key={chart.id || chart.key} chart={chart} />
        ))}
      </SimpleGrid>
    </Box>
  );
};

const AnnualDataQuality = ({
  reportTypeId = REPORT_TYPE_IDS.annual_data_quality_measurement_integrity,
  title = "Annual Data Quality Measurement Integrity",
  description = "Annual quality envelope for source, measurement, and integrity checks.",
  emptyDataMessage = "Annual Data Quality data is empty for the selected year.",
  searchPrompt = "Update filters and hit Search to retrieve annual Data Quality data.",
}) => {
  const { reportRange, reportsData, organizationId } = useSelector(
    (state) => state.defaultReducer
  );
  const report = reportsData.find(
    (item) => item.reportTypeId === reportTypeId && item.organizationId === organizationId
  );
  const envelope = useMemo(
    () => normalizeAnnualDataQualityEnvelope(report?.data),
    [report?.data]
  );

  return (
    <Box>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <FilterForm />
      {envelope.hasRenderableContent ? (
        <>
          <HStack justifyContent="space-between" alignItems="flex-start" mb="16px" gap="16px">
            <Box>
              <Heading fontSize="28px" color="#222" fontWeight="700">
                {title}
              </Heading>
              <Text fontSize="13px" color="var(--gray)" mt="4px">
                {description}
              </Text>
            </Box>
            <DownloadReport
              reportRange={reportRange || REPORT_RANGES.YEAR}
              reportTypeId={reportTypeId}
            />
          </HStack>
          <VStack alignItems="stretch" gap="12px">
            <Warnings warnings={envelope.warnings} />
            <SummaryCards cards={envelope.summaryCards} />
            <MetadataSummary envelope={envelope} />
            {envelope.tables.map((table) => (
              <EnvelopeTable key={table.id} table={table} />
            ))}
            <AnnualCharts charts={envelope.charts} />
          </VStack>
        </>
      ) : (
        <SelectBranchInfo
          message={
            envelope.hasEnvelope
              ? emptyDataMessage
              : searchPrompt
          }
        />
      )}
    </Box>
  );
};

export default AnnualDataQuality;
